import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';

export type HeroSlide = {
    id: string;
    headline: string;
    highlight?: string;
    description: string;
    ctaLabel: string;
    onCta?: () => void;
    /** Full-bleed background image (left side blank in the asset for text). */
    bgImage: string;
    /** Optional destination used by the parent page CTA handler. */
    buttonLink?: string;
};

interface Props {
    slides: HeroSlide[];
}

export default function HeroSlider({ slides }: Props) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (slides.length <= 1) return;
        const timer = setInterval(() => {
            setIndex((i) => (i + 1) % slides.length);
        }, 6500);
        return () => clearInterval(timer);
    }, [slides.length]);

    const slide = slides[index] ?? slides[0];
    if (!slide) return null;

    return (
        <section className="relative overflow-hidden rounded-[18px] shadow-[0_16px_50px_rgba(136,22,122,0.18)] sm:rounded-[22px]">
            {/* Image drives height so the full advert asset is visible (expected ~1600×533 / 3:1). */}
            <div className="relative aspect-[3/1] min-h-[200px] w-full bg-[#1a0a18] sm:min-h-[240px]">
                <img
                    src={slide.bgImage}
                    alt=""
                    className="absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-700"
                />
                {/* Stronger overlay on mobile so copy stays readable over the photo */}
                <div className="pointer-events-none absolute inset-0 bg-black/50 sm:bg-transparent" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/30 sm:bg-gradient-to-r sm:from-black/45 sm:via-black/20 sm:to-transparent" />
                <div className="relative z-10 flex h-full max-w-xl flex-col justify-center px-5 py-6 sm:px-10 sm:py-8 lg:px-12 lg:py-10">
                    <h2 className="font-[AvertaStd-Semibold] text-[22px] leading-tight text-white sm:text-[30px] lg:text-[36px]">
                        {slide.headline}{' '}
                        {slide.highlight ? (
                            <span className="text-[#FFE28A]">{slide.highlight}</span>
                        ) : null}
                    </h2>
                    <p className="mt-2 max-w-md text-[13px] leading-relaxed text-white/90 sm:mt-3 sm:text-[15px]">
                        {slide.description}
                    </p>
                    <div className="mt-4 sm:mt-5">
                        <button
                            type="button"
                            onClick={() => slide.onCta?.()}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] bg-white px-5 py-3 text-[14px] font-semibold text-ryd-primary shadow-md transition hover:scale-[1.02] hover:shadow-lg sm:w-auto"
                        >
                            {slide.ctaLabel}
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {slides.length > 1 && (
                <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
                    {slides.map((s, i) => (
                        <button
                            key={s.id}
                            type="button"
                            aria-label={`Go to slide ${i + 1}`}
                            onClick={() => setIndex(i)}
                            className={`h-2 rounded-full transition-all ${
                                i === index ? 'w-6 bg-white' : 'w-2 bg-white/45 hover:bg-white/70'
                            }`}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}
